/**
 * Course List Page
 * Lists all published courses with advanced filtering
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesService, Course, SearchCoursesParams } from '../services/coursesService';
import enrollmentService from '../services/enrollmentService';
import { useAuth } from '../contexts/AuthContext';
import AdvancedFilters from '../components/courses/AdvancedFilters';
import AddToPlaylistModal from '../components/playlist/AddToPlaylistModal';
import { usePlaylistStore } from '../stores/playlistStore';
import './CourseListPage.css';

import { resolveMediaUrl, getSmallThumbnailUrl } from '../utils/mediaUrl';
import { BilingualTitle } from '../components/common/BilingualTitle';
import { CEFRGuideModal } from '../components/cefr';
import { useSeoTags, buildCourseItemListJsonLd } from '../hooks/useSeoTags';

const PAGE_SIZE = 20;

const CourseListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { t, i18n } = useTranslation(['course', 'common']);
  const [courses, setCourses] = useState<Course[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCefrGuide, setShowCefrGuide] = useState(false);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());
  const [enrollingCourseId, setEnrollingCourseId] = useState<string | null>(null);
  const [enrollConfirmCourse, setEnrollConfirmCourse] = useState<Course | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<SearchCoursesParams | null>(null);

  // Playlist modal state
  const { openModal: openPlaylistModal } = usePlaylistStore();

  // Build filters from URL params
  const buildFiltersFromUrl = useCallback((): SearchCoursesParams => {
    const filters: SearchCoursesParams = {};
    const q = searchParams.get('q');
    const difficulty = searchParams.get('difficulty');
    const cefrLevel = searchParams.get('level') || searchParams.get('cefrLevel');
    const creatorId = searchParams.get('creatorId');
    const minRating = searchParams.get('minRating');
    const minDuration = searchParams.get('minDuration');
    const maxDuration = searchParams.get('maxDuration');
    const translationLanguage = searchParams.get('translationLanguage');
    const topic = searchParams.get('topic');
    const contentType = searchParams.get('contentType');
    const accent = searchParams.get('accent');
    const sortBy = searchParams.get('sortBy');
    const visibility = searchParams.get('visibility');

    if (q) filters.q = q;
    if (difficulty) filters.difficulty = difficulty as SearchCoursesParams['difficulty'];
    if (cefrLevel) filters.cefrLevel = cefrLevel as SearchCoursesParams['cefrLevel'];
    if (creatorId) filters.creatorId = creatorId;
    if (topic) filters.topic = topic;
    if (contentType) filters.contentType = contentType as SearchCoursesParams['contentType'];
    if (accent) filters.accent = accent;
    if (visibility === 'public' || visibility === 'private') {
      filters.visibility = visibility;
    }
    if (translationLanguage && translationLanguage !== 'all') {
      filters.translationLanguage = translationLanguage;
    } else if (!translationLanguage && user?.languagePreference) {
      filters.translationLanguage = user.languagePreference;
    }
    // translationLanguage === 'all' → no filter applied (show all)
    if (minRating) filters.minRating = Number(minRating);
    if (minDuration) filters.minDuration = Number(minDuration);
    if (maxDuration) filters.maxDuration = Number(maxDuration);
    if (sortBy) filters.sortBy = sortBy as SearchCoursesParams['sortBy'];

    return filters;
  }, [searchParams, user]);

  const [currentFilters, setCurrentFilters] = useState<SearchCoursesParams>(buildFiltersFromUrl);

  const loadCourses = useCallback(async (filters: SearchCoursesParams) => {
    setLoading(true);
    setError(null);
    filtersRef.current = filters;
    try {
      const { courses: data, pagination } = await coursesService.getCourses({
        ...filters,
        limit: PAGE_SIZE,
        offset: 0,
      });
      setCourses(data);
      setTotal(pagination.total);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
      setError(err.response?.data?.message || err.message || t('course:courseList.loading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadingMoreRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !filtersRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const currentCount = coursesRef.current.length;
      const { courses: data, pagination } = await coursesService.getCourses({
        ...filtersRef.current,
        limit: PAGE_SIZE,
        offset: currentCount,
      });
      setCourses(prev => [...prev, ...data]);
      setTotal(pagination.total);
    } catch (err: any) {
      console.error('Failed to load more courses:', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  const hasMore = courses.length < total;

  // Keep a ref to courses for loadMore offset calculation (avoids stale closure)
  const coursesRef = useRef(courses);
  coursesRef.current = courses;

  // Infinite scroll via IntersectionObserver (callback ref for reliable attach)
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Cleanup observer on unmount to prevent memory leak
  useEffect(() => {
    return () => { observerRef.current?.disconnect(); };
  }, []);

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px' },
    );
    observerRef.current.observe(node);
  }, [loadMore]);

  const loadEnrollmentStatus = useCallback(async () => {
    try {
      const enrollments = await enrollmentService.getMyEnrollments();
      const ids = new Set(enrollments.map((e) => e.courseId));
      setEnrolledCourseIds(ids);
    } catch (err) {
      console.error('Failed to load enrollment status:', err);
    }
  }, []);

  // Sync filters with URL
  useEffect(() => {
    setCurrentFilters(buildFiltersFromUrl());
  }, [buildFiltersFromUrl]);

  // Load courses when filters change
  useEffect(() => {
    loadCourses(currentFilters);
  }, [currentFilters, loadCourses]);

  useEffect(() => {
    if (isAuthenticated) {
      loadEnrollmentStatus();
    }
  }, [isAuthenticated, loadEnrollmentStatus]);

  const handleFilterChange = (newFilters: SearchCoursesParams) => {
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.q) params.set('q', newFilters.q);
    if (newFilters.difficulty) params.set('difficulty', newFilters.difficulty);
    if (newFilters.cefrLevel) params.set('level', newFilters.cefrLevel);
    if (newFilters.creatorId) params.set('creatorId', newFilters.creatorId);
    if (newFilters.translationLanguage) {
      params.set('translationLanguage', newFilters.translationLanguage);
    } else {
      // Explicit "show all" — distinguish from "no param set (use default)"
      params.set('translationLanguage', 'all');
    }
    if (newFilters.topic) params.set('topic', newFilters.topic);
    if (newFilters.contentType) params.set('contentType', newFilters.contentType);
    if (newFilters.accent) params.set('accent', newFilters.accent);
    if (newFilters.visibility) params.set('visibility', newFilters.visibility);
    if (newFilters.minRating) params.set('minRating', String(newFilters.minRating));
    if (newFilters.minDuration !== undefined) params.set('minDuration', String(newFilters.minDuration));
    if (newFilters.maxDuration !== undefined) params.set('maxDuration', String(newFilters.maxDuration));
    if (newFilters.sortBy) params.set('sortBy', newFilters.sortBy);

    setSearchParams(params);
    setCurrentFilters(newFilters);
  };

  const handleAddToPlaylist = (courseId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    openPlaylistModal(courseId);
  };

  const handleEnrollClick = (course: Course, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setEnrollConfirmCourse(course);
  };

  const handleEnrollConfirm = async () => {
    if (!enrollConfirmCourse) return;
    const courseId = enrollConfirmCourse.id;

    setEnrollingCourseId(courseId);
    try {
      await enrollmentService.enroll(courseId);
      setEnrolledCourseIds((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.add(courseId);
        return newSet;
      });
      setEnrollConfirmCourse(null);
    } catch (err: any) {
      console.error('Failed to enroll:', err);
      alert(err.message || t('course:courseList.enrollFailed'));
    } finally {
      setEnrollingCourseId(null);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getCefrBadgeClass = (cefrLevel?: string): string => {
    if (!cefrLevel) return 'unknown';
    return `cefr-${cefrLevel.toLowerCase()}`;
  };

  // Check if any filters are active (for result summary)
  const hasActiveFilters = !!(
    currentFilters.q ||
    currentFilters.difficulty ||
    currentFilters.cefrLevel ||
    currentFilters.creatorId ||
    currentFilters.translationLanguage ||
    currentFilters.topic ||
    currentFilters.contentType ||
    currentFilters.accent ||
    currentFilters.visibility ||
    currentFilters.minRating ||
    currentFilters.minDuration !== undefined ||
    currentFilters.maxDuration !== undefined
  );

  // SEO for /courses — uses the existing courseList.title/subtitle keys, plus
  // an ItemList JSON-LD of the first 10 loaded courses. The list is
  // best-effort (it's empty while `loading` is true, but Google re-crawls
  // after JS execution, and Phase 1D prerender will lock in a populated list).
  const courseListTitle =
    t('course:courseList.title', { defaultValue: 'Browse English Courses' });
  const courseListSubtitle = t('course:courseList.subtitle', {
    defaultValue: 'Discover AI-powered English learning courses across every level and topic.',
  });
  const seoTags = useSeoTags({
    title: courseListTitle,
    description: courseListSubtitle,
    canonical: '/courses',
    jsonLd: buildCourseItemListJsonLd({
      listName: courseListTitle,
      items: courses.map((c) => ({ id: c.id, title: c.title })),
    }),
  });

  if (loading) {
    return (
      <div className="course-list-page">
        {seoTags}
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('course:courseList.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="course-list-page">
      {seoTags}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => loadCourses(currentFilters)}>{t('common:retry')}</button>
        </div>
      )}

      {/* Advanced Filters */}
      <AdvancedFilters
        currentFilters={currentFilters}
        onFilterChange={handleFilterChange}
        isOpen={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
      />

      {/* Search/Filter Results Summary */}
      {hasActiveFilters && (
        <div className="search-results-header">
          <span>
            {currentFilters.q ? t('course:courseList.searchLabel', { query: currentFilters.q }) : t('course:courseList.filterResults')}
            {currentFilters.difficulty && ` ${t('course:courseList.difficultyFilter', { level: currentFilters.difficulty })}`}
            {currentFilters.minRating && ` ${t('course:courseList.ratingFilter', { rating: currentFilters.minRating })}`}
          </span>
          <span className="result-count">{t('course:courseList.resultCount', { count: total })}</span>
        </div>
      )}

      {courses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{hasActiveFilters ? '🔍' : '📚'}</div>
          <h3>{hasActiveFilters ? t('course:courseList.noResultsFiltered') : t('course:courseList.noCoursesYet')}</h3>
          <p>
            {hasActiveFilters
              ? t('course:courseList.noResultsFilteredDesc')
              : t('course:courseList.noCoursesYetDesc')}
          </p>
          {hasActiveFilters && (
            <button
              className="btn-primary"
              onClick={() => handleFilterChange({})}
            >
              {t('course:courseList.clearAllFilters')}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="courses-grid">
            {courses.map((course) => (
              <div
                key={course.id}
                className="course-card"
                onClick={() => navigate(`/courses/${course.id}`)}
              >
                {/* Add to Playlist button */}
                <button
                  className="btn-add-to-playlist"
                  onClick={(e) => handleAddToPlaylist(course.id, e)}
                  title={t('course:courseList.addToPlaylist')}
                >
                  +
                </button>
                {(course.coverImageUrl || course.thumbnailUrl) ? (
                  <div
                    className="course-cover"
                    style={{ backgroundImage: `url(${getSmallThumbnailUrl(course.coverImageUrl || course.thumbnailUrl)})` }}
                  />
                ) : (
                  <div className="course-cover placeholder">
                    <span>🎓</span>
                  </div>
                )}
                <div className="course-info">
                  <BilingualTitle
                    title={course.title}
                    titles={course.titles}
                    fallback={t('course:manage.untitledCourse')}
                    variant="card"
                    as="h3"
                    className="course-title"
                  />
                  <div className="course-meta">
                    {course.cefrLevel && (
                      <span
                        className={`badge difficulty ${getCefrBadgeClass(course.cefrLevel)} clickable`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowCefrGuide(true); }}
                      >
                        {course.cefrLevel}
                      </span>
                    )}
                    <span className="badge duration">
                      {formatDuration(course.durationSeconds)}
                    </span>
                    {(course.wordCount !== undefined && course.wordCount > 0) && (
                      <span className="badge words">{t('common:words', { count: course.wordCount })}</span>
                    )}
                    {(course as any).contentType && (
                      <span className="badge content-type-badge">
                        {String(t(`course:filters.contentTypes.${(course as any).contentType}`, { defaultValue: (course as any).contentType }))}
                      </span>
                    )}
                  </div>
                  {((course.topics?.length ?? 0) > 0 || (course as any).accent || (course.tags?.length ?? 0) > 0) && (
                    <div className="course-topic-badges">
                      {course.topics?.map((topic) => (
                        <span key={topic.code} className="badge topic-badge">
                          {topic.labels[i18n.language] || topic.labels['en'] || topic.code}
                        </span>
                      ))}
                      {(course as any).accent && (
                        <span className="badge accent-badge">
                          {String(t(`course:filters.accents.${(course as any).accent}`, { defaultValue: (course as any).accent }))}
                        </span>
                      )}
                      {course.tags?.map((tag) => (
                        <span key={`tag-${tag}`} className="badge content-tag-badge">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="course-card-footer">
                    <div className="course-stats">
                      {(course.enrollmentCount || 0) > 0 && (
                        <span className="stat">
                          {t('common:enrolled', { count: course.enrollmentCount })}
                        </span>
                      )}
                      {(course.ratingAverage || course.rating) && course.ratingCount && course.ratingCount >= 3 ? (
                        <span className="stat rating-stat">
                          {(Number(course.ratingAverage) || Number(course.rating) || 0).toFixed(1)}
                          <span className="rating-count">({course.ratingCount})</span>
                        </span>
                      ) : null}
                      {course.creator && (course.creator.fullName || course.creator.username) && (
                        <span className="stat creator-stat">
                          <Link
                            to={`/profile/${course.creator.id}`}
                            className="creator-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {course.creator.fullName || course.creator.username}
                          </Link>
                        </span>
                      )}
                    </div>
                    <div className="course-actions">
                      {enrolledCourseIds.has(course.id) ? (
                        <span className="enrolled-badge">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          {t('course:courseList.enrolled')}
                        </span>
                      ) : (
                        <button
                          className="btn-enroll"
                          onClick={(e) => handleEnrollClick(course, e)}
                          disabled={enrollingCourseId === course.id}
                        >
                          {enrollingCourseId === course.id ? t('course:courseList.enrolling') : t('course:courseList.enroll')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="load-more-container" ref={sentinelRef}>
              {loadingMore && <span className="load-more-spinner" />}
              <span className="load-more-count">
                {courses.length} / {total}
              </span>
            </div>
          )}
        </>
      )}

      {/* Add to Playlist Modal */}
      <AddToPlaylistModal />
      <CEFRGuideModal isOpen={showCefrGuide} onClose={() => setShowCefrGuide(false)} />

      {/* Enroll Confirmation Modal */}
      {enrollConfirmCourse && (
        <div className="enroll-modal-overlay" onClick={() => setEnrollConfirmCourse(null)}>
          <div className="enroll-modal" onClick={(e) => e.stopPropagation()}>
            {enrollConfirmCourse.thumbnailUrl && (
              <img
                className="enroll-modal-thumbnail"
                src={resolveMediaUrl(enrollConfirmCourse.thumbnailUrl)}
                alt=""
              />
            )}
            <h3 className="enroll-modal-title">
              <BilingualTitle
                  title={enrollConfirmCourse.title}
                  titles={enrollConfirmCourse.titles}
                  fallback={t('course:manage.untitledCourse')}
                />
            </h3>
            <p className="enroll-modal-desc">{t('course:enrollment.enrollToLearnDesc')}</p>
            <div className="enroll-modal-actions">
              <button
                className="enroll-modal-cancel"
                onClick={() => setEnrollConfirmCourse(null)}
              >
                {t('common:cancel')}
              </button>
              <button
                className="enroll-modal-confirm"
                onClick={handleEnrollConfirm}
                disabled={enrollingCourseId === enrollConfirmCourse.id}
              >
                {enrollingCourseId === enrollConfirmCourse.id
                  ? t('course:courseList.enrolling')
                  : t('course:enrollment.confirmSelection')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseListPage;
